import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { X, Cookie } from "lucide-react";
import { Link } from "react-router-dom";

// Extend Window interface for GA4
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      // Show banner after a short delay for better UX
      setTimeout(() => setIsVisible(true), 1000);
    } else if (consent === "accepted") {
      // Load GA4 if previously accepted
      loadGA4();
    }
  }, []);

  const loadGA4 = () => {
    // Only load GA4 if measurement ID is configured
    const measurementId = "YOUR_GA4_MEASUREMENT_ID";
    
    if (measurementId !== "YOUR_GA4_MEASUREMENT_ID") {
      // Create and append GA4 script
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);

      // Initialize GA4
      window.dataLayer = window.dataLayer || [];
      function gtag(...args: any[]) {
        window.dataLayer.push(args);
      }
      gtag("js", new Date());
      gtag("config", measurementId, {
        anonymize_ip: true,
        cookie_flags: "SameSite=None;Secure",
      });
    }
  };

  const handleAccept = () => {
    localStorage.setItem("cookieConsent", "accepted");
    loadGA4();
    closeConsent();
  };

  const handleDecline = () => {
    localStorage.setItem("cookieConsent", "declined");
    closeConsent();
  };

  const closeConsent = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  if (!isVisible) return null;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 p-4 transition-all duration-300 ${
        isClosing ? "translate-y-full opacity-0" : "translate-y-0 opacity-100"
      }`}
    >
      <Card className="mx-auto max-w-4xl border-primary/20 bg-background/95 backdrop-blur-sm shadow-xl">
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 mt-1">
              <Cookie className="h-6 w-6 text-primary" />
            </div>
            
            <div className="flex-1 space-y-4">
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-foreground">
                  🍪 Cookie-Einstellungen
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Wir verwenden Cookies und ähnliche Technologien, um unsere Website zu verbessern 
                  und Ihr Nutzererlebnis zu optimieren. Mit "Alle akzeptieren" stimmen Sie der 
                  Verwendung von Analytics-Cookies zu, die uns helfen zu verstehen, wie Besucher 
                  unsere Website nutzen. Sie können Ihre Einwilligung jederzeit widerrufen.
                </p>
                <p className="text-xs text-muted-foreground">
                  Weitere Informationen finden Sie in unserer{" "}
                  <Link to="/datenschutz" className="text-primary hover:underline">
                    Datenschutzerklärung
                  </Link>
                  .
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleAccept}
                  className="flex-1 sm:flex-none"
                  size="lg"
                >
                  Alle akzeptieren
                </Button>
                <Button
                  onClick={handleDecline}
                  variant="outline"
                  className="flex-1 sm:flex-none"
                  size="lg"
                >
                  Nur notwendige
                </Button>
              </div>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={closeConsent}
              className="flex-shrink-0"
              aria-label="Schließen"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
